import { ChangeDetectionStrategy, Component, inject, OnDestroy, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import type { ToolInformationDialogData } from '@data-terminal/feature-dialogs/services/tool-information-dialog/tool-information-dialog.service';
import { ToolPart, type ToolUsageEntity } from '@data-terminal/shared-models/tool-usage';
import { HdmuiKeyValueVerticalComponent } from '@heidelberg/hdmui-angular';
import { TranslateModule } from '@ngx-translate/core';

const TOOL_INFORMATION_DIALOG_AUTO_CLOSE_MS = 5000;
const TOOL_INFORMATION_DIALOG_COUNTDOWN_INTERVAL_MS = 1000;

@Component({
    selector: 'data-terminal-tool-information-dialog',
    imports: [HdmuiKeyValueVerticalComponent, MatButtonModule, MatCardModule, TranslateModule, MatDivider],
    templateUrl: './tool-information-dialog.component.html',
    styleUrl: './tool-information-dialog.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToolInformationDialogComponent implements OnDestroy {
    readonly #dialogRef = inject(MatDialogRef<ToolInformationDialogComponent>);
    readonly #autoCloseTimer = setTimeout(() => this.close(), TOOL_INFORMATION_DIALOG_AUTO_CLOSE_MS);
    readonly #countdownTimer = setInterval(
        () => this.autoCloseSeconds.update((seconds) => Math.max(seconds - 1, 1)),
        TOOL_INFORMATION_DIALOG_COUNTDOWN_INTERVAL_MS
    );
    readonly dialogData: ToolInformationDialogData = inject(MAT_DIALOG_DATA);

    public readonly autoCloseSeconds = signal(
        TOOL_INFORMATION_DIALOG_AUTO_CLOSE_MS / TOOL_INFORMATION_DIALOG_COUNTDOWN_INTERVAL_MS
    );

    public readonly punchingToolUsage: ToolUsageEntity | undefined = this.dialogData.toolUsages.find(
        (toolUsage) => toolUsage.toolPart === ToolPart.PUNCHING
    );
    public readonly strippingToolUsage: ToolUsageEntity | undefined = this.dialogData.toolUsages.find(
        (toolUsage) => toolUsage.toolPart === ToolPart.STRIPPING
    );
    public readonly toolUsages = [this.punchingToolUsage, this.strippingToolUsage].filter(
        (toolUsage): toolUsage is ToolUsageEntity => !!toolUsage
    );

    public close(): void {
        clearTimeout(this.#autoCloseTimer);
        clearInterval(this.#countdownTimer);
        this.#dialogRef.close();
    }

    public ngOnDestroy(): void {
        clearTimeout(this.#autoCloseTimer);
        clearInterval(this.#countdownTimer);
    }
}
